import axios from 'axios'
import store from '@/store'
import storage from 'store'
import notification from 'ant-design-vue/es/notification'
import message from 'ant-design-vue/es/message'
import { VueAxios } from './axios'
import { ACCESS_TOKEN } from '@/store/mutation-types'
import { tansParams, blobValidate } from "@/utils/ruoyi";
import cache from '@/plugins/cache'
import errorCode from '@/utils/errorCode'
import qs from 'qs'
import { saveAs } from 'file-saver'
import { redirectToLogin } from '@/utils/loginRedirect'

// 是否显示重新登录
let isReloginShow
// 会话已过期：拦截后续业务请求，避免过期后继续打满连接池拖慢跳登录
let sessionExpired = false
const pendingSources = new Set()
const loginWhiteApis = ['/login', '/captchaImage', '/register', '/get-phone-valid-code', '/login-by-phone']

function isLoginWhiteApi (url) {
  if (!url) return false
  const path = String(url)
  return loginWhiteApis.some(api => path.indexOf(api) !== -1)
}

function releaseCancelSource (config) {
  if (config && config.__cancelSource) {
    pendingSources.delete(config.__cancelSource)
    config.__cancelSource = null
  }
}

function cancelPendingRequests () {
  pendingSources.forEach(source => {
    try {
      source.cancel('会话已过期')
    } catch (e) {}
  })
  pendingSources.clear()
}

function handleSessionExpired (msg) {
  if (isReloginShow) return
  isReloginShow = true
  sessionExpired = true
  notification.error({ message: msg || '登录状态已过期，请重新登录' })
  try {
    const { closeWebSocket } = require('@/utils/socket')
    closeWebSocket()
  } catch (e) {}
  cancelPendingRequests()
  // 同步清本地并立刻跳转，不调 /logout、不进 finally 排队
  store.dispatch('Logout', { remote: false })
  redirectToLogin()
  isReloginShow = false
}

axios.defaults.headers['Content-Type'] = 'application/json;charset=utf-8'
// 创建 axios 实例
const request = axios.create({
  // API 请求的默认前缀
  baseURL: process.env.VUE_APP_BASE_API,
  timeout: 100000 // 请求超时时间
})

// 异常拦截处理器
const errorHandler = (error) => {
  if (error && error.config) {
    releaseCancelSource(error.config)
  }
  if (axios.isCancel(error) || (error && error.message === '会话已过期')) {
    return Promise.reject(error)
  }
  if (error && error.message === '数据正在处理，请勿重复提交') {
    return Promise.reject(error)
  }
  console.log('err' + error)
  let { message } = error
  if (message === 'Network Error') {
    message = '后端接口连接异常'
  } else if (message && message.includes('timeout')) {
    message = '系统接口请求超时'
  } else if (message && message.includes('Request failed with status code')) {
    message = '系统接口' + message.substr(message.length - 3) + '异常'
  }
  notification.error({
    message: message,
    duration: 5 * 1000
  })
  return Promise.reject(error)
}

// request interceptor
request.interceptors.request.use(config => {
  const token = storage.get(ACCESS_TOKEN)
  // 重新登录成功后恢复
  if (token && sessionExpired) {
    sessionExpired = false
  }
  // 过期后只放行登录相关接口
  if (sessionExpired && !isLoginWhiteApi(config.url)) {
    return Promise.reject(new Error('会话已过期'))
  }
  // 是否需要防止数据重复提交（支持 headers 或 request 配置项；兼容字符串 "false"）
  const repeatSubmitFlag = (config.headers || {}).repeatSubmit !== undefined
    ? (config.headers || {}).repeatSubmit
    : config.repeatSubmit
  const isRepeatSubmit = repeatSubmitFlag === false || repeatSubmitFlag === 'false'
  // 如果 token 存在
  // 让每个请求携带自定义 token 请根据实际情况自行修改
  if (token) {
    config.headers['Authorization'] = 'Bearer ' + token // 让每个请求携带自定义token 请根据实际情况自行修改
    // config.headers['accessAccess-Token'] = token
  }
  // 登记可取消请求，过期时批量 abort，腾出带宽给登录页
  const source = axios.CancelToken.source()
  if (config.cancelToken) {
    config.cancelToken.promise.then(reason => {
      try { source.cancel(reason) } catch (e) {}
    })
  }
  config.cancelToken = source.token
  config.__cancelSource = source
  pendingSources.add(source)
  // 处理params参数
  if (config.params) {
    const url = config.url + '?' + qs.stringify(config.params, { indices: false })
    config.params = {}
    config.url = url
  }
  // get请求映射params参数
  if (config.method === 'get' && config.params) {
    let url = config.url + '?' + tansParams(config.params);
    url = url.slice(0, -1);
    config.params = {};
    config.url = url;
  }
  if (!isRepeatSubmit && (config.method === 'post' || config.method === 'put' || config.method === 'delete')) {
    const requestObj = {
      url: config.url,
      data: typeof config.data === 'object' ? JSON.stringify(config.data) : config.data,
      time: new Date().getTime()
    }
    const sessionObj = cache.session.getJSON('sessionObj')
    if (sessionObj === undefined || sessionObj === null || sessionObj === '') {
      cache.session.setJSON('sessionObj', requestObj)
    } else {
      const s_url = sessionObj.url;                  // 请求地址
      const s_data = sessionObj.data;                // 请求数据
      const s_time = sessionObj.time;                // 请求时间
      const interval = 1000;                         // 间隔时间(ms)，小于此时间视为重复提交
      if (s_data === requestObj.data && requestObj.time - s_time < interval && s_url === requestObj.url) {
        const message = '数据正在处理，请勿重复提交';
        console.warn(`[${s_url}]: ` + message)
        return Promise.reject(new Error(message))
      } else {
        cache.session.setJSON('sessionObj', requestObj)
      }
    }
  }
  return config
}, errorHandler)

// response interceptor
request.interceptors.response.use((res) => {
  releaseCancelSource(res.config)
  // console.log('响应数据', res)
  // 请求rul
  const requestUrl = res.config.url
  // 未设置状态码则默认成功状态
  const code = res.data.code || '20001'
  // 获取错误信息
  const msg = errorCode[code] || res.data.message || res.data.msg|| errorCode['default']
  // 二进制数据则直接返回
  if (res.request.responseType === 'blob' || res.request.responseType === 'arraybuffer') {
    return res.data
  }
  if (code === 401 || code === 'U0231' || code === 'U0230') {
    // /logout 自身返回过期码时不再递归登出
    if (requestUrl && String(requestUrl).indexOf('/logout') !== -1) {
      return Promise.reject(new Error(msg))
    }
    handleSessionExpired('登录状态已过期，请重新登录')
    return Promise.reject(new Error(msg))
  } else if (code === 500) {
    if (requestUrl !== '/login') {
      notification.error({
        message: msg,
        description: msg
      })
    }
  } else if (code === 601) {
    notification.error({
      message: msg
    })
  } else if(code === "40000" || code === "U0200" || code === "U0210") {
    notification.error({
      message: msg
    })
    return Promise.reject(new Error(msg))
  } else if(code === "20009") {
		console.log('走这里了20009')
    return res.data
  } else if (code !== '20001' && code !== 1) {
    notification.error({ message: msg })
    return Promise.reject(new Error('error'))
  } else {
    return res.data
  }
}, errorHandler)

const installer = {
  vm: {},
  install(Vue) {
    Vue.use(VueAxios, request)
  }
}

// 通用下载方法
export function download(url, params, filename) {
  const notificationKey = 'download'
  notification.open({
    key: notificationKey,
    message: '正在下载数据，请稍候',
    duration: null,
    icon: h => {
      return h(
        'a-icon',
        {
          props: {
            type: 'loading'
          }
        }
      )
    }
  })
  return request.post(url, params, {
    transformRequest: [(params) => { return qs.stringify(params, { indices: false }) }],
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    responseType: 'blob'
  }).then(async (data) => {
    const isLogin = await blobValidate(data)
    if (isLogin) {
      const blob = new Blob([data])
      saveAs(blob, filename)
      message.success('下载成功')
    } else {
      const resText = await data.text()
      const rspObj = JSON.parse(resText)
      const errMsg = errorCode[rspObj.code] || rspObj.msg || errorCode['default']
      message.error(errMsg)
    }
    notification.close(notificationKey)
  }).catch((r) => {
    message.error('下载文件出现错误，请联系管理员！')
    notification.close(notificationKey)
  })
}

export default request

export {
  installer as VueAxios,
  request as axios
}
